/* eslint-disable max-classes-per-file */
import {
	Cartesian3,
	Color,
	ComponentDatatype,
	GeometryInstance,
	GeometryInstanceAttribute,
	GroundPolylineGeometry,
	Material,
	MaterialAppearance,
	PerInstanceColorAppearance,
	PolygonGeometry,
	ShowGeometryInstanceAttribute,
	PolygonHierarchy,
} from 'cesium';
import { v4 as uuidv4, validate as validateUUID } from 'uuid';
import { HierarchyItem } from './HierarchyItem';
import { Dataset, createDatasetFromPlainDataset } from './Dataset';

class Style {
	constructor() {
		this.fillColor = new Color(1, 1, 0, 0.5);
		this.lineColor = Color.PINK;
		this.lineWidth = 2;
	}
}

/**
 * A 3D entity in the application
 * @class
 * @extends HierarchyItem
 */
class Entity extends HierarchyItem {
	/**
	 * @constructor
	 * @param {Object} entityProperties
	 * @param {Entity} [entityProperties.parent=null] parent to assign entity to
	 * @param {String} [entityProperties.uuid=new uuid] the uuid of the entity
	 * @param {String} [entityProperties.type="generic"] the base type of the entity
	 * @param {String} [entityProperties.name="Unnamed Element"] the name of the entity
	 * @param {String} [entityProperties.description] the description of the entity
	 * @param {String[]} [entityProperties.tags] the tags assigned to the entity
	 * @param {Object} [entityProperties.data] freeform entity data
	 * @param {DateString} [entityProperties.createdAt] creation date of the entity on the backend
	 * @param {DateString} [entityProperties.updatedAt] update date of the entity on the backend
	 */
	constructor(entityProperties = {}) {
		super();

		// validate given properties
		if (entityProperties.uuid && !validateUUID(entityProperties.uuid)) {
			throw new TypeError('Expected given uuid to be a valid uuid or null');
		}
		if (entityProperties.name && typeof entityProperties.name !== 'string') {
			throw new TypeError('Expected given name to be a string or null');
		}
		if (entityProperties.type && typeof entityProperties.type !== 'string') {
			throw new TypeError('Expected given type to be a string or null');
		}

		if (entityProperties.description && typeof entityProperties.description !== 'string') {
			throw new TypeError('Expected given description to be a string or null');
		}
		if (
			entityProperties.tags &&
			(!Array.isArray(entityProperties.tags) || entityProperties.tags.some((tag) => typeof tag !== 'string'))
		) {
			throw new TypeError('Expected given tags to be an array of strings');
		}

		// apply default values
		entityProperties = {
			id: uuidv4(),
			name: 'Unnamed Element',
			type: 'generic',
			description: '',
			tags: [],
			data: [],
			geometry: { coordinates: [0, 0] },
			createdAt: null,
			updatedAt: null,
			...entityProperties,
		};

		/**
		 * @member {String} Entity#uuid
		 * @readonly
		 */
		this.id = entityProperties.id;

		/**
		 * @member {String} Entity#type
		 * @readonly
		 */
		this.type = entityProperties.type;

		/** @member {String} Entity#name user definable name */
		this.name = entityProperties.name;

		/** @member {String} Entity#description user definable description */
		this.description = entityProperties.description;

		/** @member {String[]} Entity#tags a list of tags categorizing this entity */
		this.tags = entityProperties.tags;

		/** @member {Dataset[]} Entity#data user definable entity data */
		this.data = entityProperties.data.map((d) => (d instanceof Dataset ? d : createDatasetFromPlainDataset(d)));

		/** @member {?Date} Entity#createdAt the time when the entity was created on the backend, null if the entity was not synced yet */
		this.createdAt = new Date(entityProperties.createdAt);

		/** @member {?Date} Entity#updatedAt the last time the entity got updated on the backend, null if the entity was not synced yet */
		this.updatedAt = new Date(entityProperties.updatedAt);

		/** @member {Number[][]} Entity#rawVertices The vertices of this entity as raw GeoJSON coordinates */
		// eslint-disable-next-line prefer-destructuring
		this.rawVertices = entityProperties.geometry.coordinates[0];

		/** @member {Cesium.Cartesian3[]} Entity#vertices The vertices of this entity */
		// eslint-disable-next-line prefer-destructuring
		this.vertices = Cartesian3.fromDegreesArray(entityProperties.geometry.coordinates.flat(Infinity));

		/** @member {Cesium.GeometryInstance[]} Entity#geometryInstaces The cesium geometry representation of this entity */
		this.geometryInstances = [];

		/** @member {Style} Entity#style The visual appearance of the entity */
		this.style = new Style();

		if (entityProperties.parent instanceof Entity) this.parent = entityProperties.parent;
	}

	/**
	 * @method Entity#updateStyle
	 * @abstract
	 */
	updateStyles() {
		for (const geo of this.geometryInstances) geo.attributes.show = new ShowGeometryInstanceAttribute();
	}

	/**
	 * @method Entity#updateGeometry
	 * @abstract
	 */
	// eslint-disable-next-line
	updateGeometry() {}
}

class LineString extends Entity {
	/**
	 * Appearance shader for all linestrings
	 * @static
	 */
	static APPEARANCE = new MaterialAppearance({
		closed: false,
		material: Material.fromType('Color'),
		flat: true,
		translucent: false,
	});

	/**
	 * @member {Number} LineString#length The length of the linestring in meters
	 * @readonly
	 */
	get length() {
		let length = 0;
		for (let i = 1; i < this.vertices.length; i++)
			length += Cartesian3.distance(this.vertices[i - 1], this.vertices[i]);
		return length;
	}

	updateStyles() {
		Entity.prototype.updateStyles.apply(this, arguments);
		this.geometryInstances[0].attributes.color = new GeometryInstanceAttribute({
			componentDatatype: ComponentDatatype.UNSIGNED_BYTE,
			componentsPerAttribute: 4,
			normalize: true,
			value: this.style.lineColor.toBytes(),
		});
	}

	updateGeometry() {
		const geometry = new GroundPolylineGeometry({ positions: this.vertices, width: this.style.lineWidth });
		if (!this.geometryInstances.length) {
			this.geometryInstances.push(new GeometryInstance({ geometry, id: this.id }));
			this.updateStyles();
		} else this.geometryInstances[0].geometry = geometry;
	}
}

class Polygon extends LineString {
	/**
	 * Appearance shader for all polygons
	 * @static
	 */
	static APPEARANCE = new PerInstanceColorAppearance({
		closed: false,
		flat: true,
	});

	/**
	 * @member {Number} Polygon#area The area of the polygon in square meters
	 * @readonly
	 */
	get area() {
		// @TODO: properly implement polygon area calculation
		return this.vertices.length * 10;
	}

	// eslint-disable-next-line
	get key () { return 'polygon' }

	updateStyles() {
		LineString.prototype.updateStyles.apply(this, arguments);
		if (this.geometryInstances.length > 1) {
			this.geometryInstances[1].attributes.color = new GeometryInstanceAttribute({
				componentDatatype: ComponentDatatype.UNSIGNED_BYTE,
				componentsPerAttribute: 4,
				normalize: true,
				value: this.style.fillColor.toBytes(),
			});
		}
	}

	updateGeometry() {
		LineString.prototype.updateGeometry.apply(this, arguments);
		const geometry = PolygonGeometry.fromPositions({ positions: this.vertices });
		if (this.geometryInstances.length === 1) {
			this.geometryInstances[1] = new GeometryInstance({ geometry, id: this.id });
			this.updateStyles();
		} else this.geometryInstances[1].geometry = geometry;
	}
}

class MultiPolygon extends Entity {
	/**
	 * Appearance shader for all polygons
	 * @static
	 */
	static APPEARANCE = new PerInstanceColorAppearance({
		closed: false,
		flat: true,
	});
	// eslint-disable-next-line
	get key () { return 'multipolygon' }

	updateStyles() {
		Entity.prototype.updateStyles.apply(this, arguments);
		this.geometryInstances[0].attributes.color = new GeometryInstanceAttribute({
			componentDatatype: ComponentDatatype.UNSIGNED_BYTE,
			componentsPerAttribute: 4,
			normalize: true,
			value: this.style.fillColor.toBytes(),
		});
	}

	updateGeometry() {
		Entity.prototype.updateGeometry.apply(this, arguments);
		const holes = [];
		let geometry;

		if (this.rawVertices.length > 1) {
			this.holesLayer = true;
			const geo = Cartesian3.fromDegreesArray(this.rawVertices[0].flat(Infinity));
			for (let i = 1, len = this.rawVertices.length; i < len; i++) {
				holes.push(new PolygonHierarchy(Cartesian3.fromDegreesArray(this.rawVertices[i].flat(Infinity))));
			}
			geometry = new PolygonGeometry({
				polygonHierarchy: new PolygonHierarchy(geo, holes),
			});
		} else {
			geometry = PolygonGeometry.fromPositions({
				positions: Cartesian3.fromDegreesArray(this.rawVertices[0].flat(Infinity)),
			});
		}
		this.geometryInstances[0] = new GeometryInstance({ geometry, id: this.id });
		this.updateStyles();
	}
}
class Zone extends MultiPolygon {
	// eslint-disable-next-line class-methods-use-this
	get key() {
		return 'zone';
	}

	updateStyles() {
		if (!this.holesLayer) {
			this.style.fillColor.red = Math.floor(Math.random() * (1 - 0.2 + 1) + 0.2);
			this.style.fillColor.green = Math.floor(Math.random() * (1 - 0.2 + 1) + 0.2);
			this.style.fillColor.blue = Math.floor(Math.random() * (1 - 0.2 + 1) + 0.2);
			this.style.fillColor.alpha = 0.2;
		} else {
			this.style.fillColor.red = Math.floor(Math.random() * (1 - 0.1 + 1) + 0.1);
			this.style.fillColor.green = Math.floor(Math.random() * (1 - 0.1 + 1) + 0.1);
			this.style.fillColor.blue = Math.floor(Math.random() * (1 - 0.5 + 1) + 0.5);
			this.style.fillColor.alpha = 0.2;
		}
		MultiPolygon.prototype.updateStyles.apply(this, arguments);
	}
}
class Parcel extends Polygon {
	get key() {
		if (this.tags.includes('commercial')) return 'parcel-commercial';
		if (this.tags.includes('residential')) return 'parcel-residential';
		return 'parcel';
	}

	updateStyles() {
		if (this.tags.includes('commercial')) {
			this.style.fillColor.red = 0.4;
			this.style.fillColor.green = 0.4;
			this.style.fillColor.blue = 1.0;
			this.style.fillColor.alpha = 0.6;
		} else if (this.tags.includes('residential')) {
			this.style.fillColor.red = 1.0;
			this.style.fillColor.green = 0.4;
			this.style.fillColor.blue = 0.8;
			this.style.fillColor.alpha = 0.6;
		} else {
			this.style.fillColor.red = Math.floor(Math.random() * (1 - 0.1 + 1) + 0.1);
			this.style.fillColor.green = Math.floor(Math.random() * (1 - 0.1 + 1) + 0.1);
			this.style.fillColor.blue = Math.floor(Math.random() * (1 - 0.1 + 1) + 0.1);
			this.style.fillColor.alpha = 0.3;
		}
		Polygon.prototype.updateStyles.apply(this, arguments);
	}
}

class OSMBuilding extends Polygon {
	// eslint-disable-next-line class-methods-use-this
	get key() {
		return 'osmBuilding';
	}

	updateStyles() {
		this.style.fillColor.red = Math.floor(Math.random() * (1 - 0.2 + 1) + 0.2);
		this.style.fillColor.green = Math.floor(Math.random() * (1 - 0.2 + 1) + 0.2);
		this.style.fillColor.blue = Math.floor(Math.random() * (1 - 0.2 + 1) + 0.2);
		this.style.fillColor.alpha = 0.6;

		Polygon.prototype.updateStyles.apply(this, arguments);
	}
}

const BaseEntityTypes = {
	Point: Entity,
	MultiLineString: Entity,
	LineString,
	Polygon,
	MultiPolygon,
	OSMBuilding,
};

export function createEntityFromPlainEntity(plainEntity) {
	switch (plainEntity.type) {
		case 'parcel':
			return new Parcel(plainEntity);
		case 'zone':
			return new Zone(plainEntity);
		case 'osmBuilding':
			return new OSMBuilding(plainEntity);
	}
	return new BaseEntityTypes[plainEntity.geometry.type](plainEntity);
}

export { Style, Entity, LineString, Polygon, MultiPolygon, Parcel, Zone, OSMBuilding };
